import { useCallback, useRef, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import { queryKeys } from '@/api/query-keys'
import {
  TEXT_TO_SHAPE_INELIGIBLE_MESSAGE,
  TEXT_TO_SHAPE_NO_SELECTION_MESSAGE,
  isTextToShapeStructurallyBlocked,
  meetsTextToShapeSizeAndLengthRule,
} from '@/lib/text/text-to-shape-eligibility'
import { buildTextShapeOutline } from '@/lib/text/text-to-shape-outline'
import { buildShapeFromTextOutline } from '@/lib/text/text-to-shape-conversion'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { fractionalIndexMidpoint } from '@/lib/elements/stowed-fractional-index'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { toast } from 'sonner'
import {
  buildStowedElement,
  buildStowedVisibleRectOptions,
} from '@/lib/elements/stowed-element-builder'
import { convertShapeToPenPath } from '@/lib/elements/shape-to-pen-path'
import {
  beginPendingGroupPersist,
  claimPendingGroupPersist,
  isPendingGroupPersistCurrent,
} from '@/lib/groups/pending-group-persist'
import { persistForwardGroupApiFromShapes } from '@/lib/groups/persist-forward-group-api'
import { logger } from '@/utils/logger'
import type { ShapeHistoryLifecycleMeta } from '@/lib/history/types/shape-history-lifecycle-meta'
import type { PhotoDropBlockedReason } from '@/contexts/photo-placement-context'
import {
  LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE,
  isPhotoDropBlockedByTextChain,
} from '@/lib/photos/linked-text-photo-drop-policy'
import { notifyPhotoPlacementChangedOnSpread } from '@/lib/photos/schedule-album-photos-refresh'
import type { PhotoCandid } from '@/api/types/photo'
import { photosService } from '@/api/services/photos.service'
import {
  buildShapePhotoFill,
  isPortraitPlacedPhoto,
  resolvePlacedPhotoIdentity,
  supportsPhotoFill,
} from '@/lib/photos/fill-shape-with-photo'
import { meetsMinimumResolution } from '@/lib/photos/photo-fill-resolution'
import { buildPlacedPhotoShape } from '@/lib/photos/build-placed-photo-shape'
import { reconcilePhotoPlacementOverridesWithShapes } from '@/lib/photos/sync-photo-placement-from-shapes'
import { computeLegacyReplacementFrameSize } from '@/lib/photos/photo-placement-geometry'
import type { ClikArtDropBlockedReason } from '@/contexts/clikart-placement-context'
import {
  LINKED_TEXT_CLIKART_DROP_BLOCKED_MESSAGE,
  isClikArtDropBlockedByTextChain,
} from '@/lib/clikart/linked-text-clikart-drop-policy'
import type { ClikArtItem } from '@/api/types/clikart'
import { buildShapeClikArtFill } from '@/lib/clikart/build-clikart-shape-fill'
import { isStandaloneClikArtShape } from '@/lib/clikart/is-standalone-clikart-shape'
import { buildPlacedClikArtShape } from '@/lib/clikart/build-placed-clikart-shape'
import { templatesService } from '@/api/services/templates.service'
import { clikArtService } from '@/api/services/clikart.service'
import type { TemplateListItem } from '@/lib/templates/types/template-list-item'
import {
  reconcileClikArtPlacementsWithShapes,
  resolvePlacedClikArtId,
} from '@/lib/clikart/sync-clikart-placement-from-shapes'
import { FROZEN_CONVERT_MESSAGE } from '@/lib/editor/freeze-object-messages'
import { buildStowedShapeElement } from '../../helpers/build-stowed-shape-element'
import { createSerialRunner } from '../../handlers/create-serial-runner'
import { placeTemplate } from '../../handlers/place-template'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { OnBatchShapesCommitted } from '@/lib/transforms/types/on-batch-shapes-committed'
import { resolvePlacedImagePreviews } from '../../handlers/resolve-placed-image-previews'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeHistory,
  HomePersistence,
  HomeTextChains,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/** A placed template's ClikArt and photos arrive as ids; this gives them the preview they draw. */
const resolveTemplateImagePreviews = (shapes: ShapeData[]) =>
  resolvePlacedImagePreviews(shapes, {
    getClikArtById: clikArtService.getClikArtById,
    getCandidDetail: photosService.getCandidDetail,
  })

/**
 * Shape editing commands: selection history, nudge, shape created, Edit Path and its revert,
 * Convert Text to Shape, text on a path, crop and photo effects, and the photo / ClikArt fill,
 * remove, background-removal and drop flows with their warning dialogs.
 */
export function useHomeShapeEditing(
  deps: Pick<
    HomeFoundation,
    | 'textChains'
    | 'setTextChains'
    | 'setUnifiedLayerIds'
    | 'queryClient'
    | 'shapeManagerRef'
    | 'shapeTextBakeRef'
    | 'centerCanvasRef'
    | 'leftPanelRef'
    | 'fontLookupRef'
    | 'fontFamiliesRef'
    | 'textChainsRef'
    | 'deleteGestureScopeRef'
    | 'unifiedLayerIdsRef'
  > &
    Pick<
      HomeZoomAndSpread,
      | 'photoSizeWarningState'
      | 'setPhotoSizeWarningState'
      | 'textToImageWarningState'
      | 'setTextToImageWarningState'
      | 'clikArtSizeWarningState'
      | 'setClikArtSizeWarningState'
      | 'clikArtTextToImageWarningState'
      | 'setClikArtTextToImageWarningState'
      | 'spreadIdRef'
    > &
    Pick<HomeDocumentLayout, 'resolvedLayout' | 'spreadPlacementBounds' | 'spreadProjection'> &
    Pick<HomeToolsAndSelection, 'activeObjectsForMenus' | 'setActiveItem' | 'selectedPhotoId'> &
    Pick<
      HomePersistence,
      | 'pushAction'
      | 'popIfMatches'
      | 'enqueueAdd'
      | 'enqueueUpdate'
      | 'enqueueDelete'
      | 'flushQueue'
      | 'enqueueGroup'
      | 'enqueueRestructure'
      | 'markEdited'
      | 'isGroupShape'
      | 'isGroupTxn'
      | 'syncActiveFromShapeSelection'
    > &
    Pick<HomeHistory, 'runHistoryStep'> &
    Pick<
      HomeTextChains,
      | 'mintTextFrameUid'
      | 'mintedTextFrameUidHighWaterRef'
      | 'reserveTextFrameUidsThrough'
      | 'persistChainRider'
    >
) {
  const {
    textChains,
    setTextChains,
    setUnifiedLayerIds,
    queryClient,
    shapeManagerRef,
    shapeTextBakeRef,
    centerCanvasRef,
    leftPanelRef,
    fontLookupRef,
    fontFamiliesRef,
    textChainsRef,
    deleteGestureScopeRef,
    unifiedLayerIdsRef,
    photoSizeWarningState,
    setPhotoSizeWarningState,
    textToImageWarningState,
    setTextToImageWarningState,
    clikArtSizeWarningState,
    setClikArtSizeWarningState,
    clikArtTextToImageWarningState,
    setClikArtTextToImageWarningState,
    spreadIdRef,
    resolvedLayout,
    spreadPlacementBounds,
    spreadProjection,
    activeObjectsForMenus,
    setActiveItem,
    selectedPhotoId,
    mintTextFrameUid,
    mintedTextFrameUidHighWaterRef,
    reserveTextFrameUidsThrough,
    persistChainRider,
    pushAction,
    popIfMatches,
    enqueueAdd,
    enqueueUpdate,
    enqueueDelete,
    flushQueue,
    enqueueGroup,
    enqueueRestructure,
    markEdited,
    isGroupShape,
    isGroupTxn,
    syncActiveFromShapeSelection,
    runHistoryStep,
  } = deps

  const handleShapeSelectionHistory = useCallback(
    (beforeIds: string[], afterIds: string[], meta?: ShapeSelectionHistoryMeta) => {
      pushAction({
        type: 'selection',
        beforeIds,
        afterIds,
        beforeTextIds: meta?.beforeTextIds,
        afterTextIds: meta?.afterTextIds,
        multiSelectChromeBefore: meta?.beforeChrome,
        multiSelectChromeAfter: meta?.afterChrome,
        selectionSource: meta?.source,
        selectionGesture: meta?.gesture,
        historyTxnId: meta?.historyTxnId,
      })
    },
    [pushAction]
  )

  const handleShapesNudge = useCallback(
    (
      items: Array<{
        shapeId: string
        before: { x: number; y: number }
        after: { x: number; y: number }
      }>
    ) => {
      const mgr = shapeManagerRef.current

      if (!mgr || items.length === 0) return
      pushAction({
        type: 'batchUpdate',
        items: items.map((i) => ({
          shapeId: i.shapeId,
          before: { x: i.before.x, y: i.before.y },
          after: { x: i.after.x, y: i.after.y },
        })),
      })
      for (const i of items) {
        mgr.updateShape(i.shapeId, { x: i.after.x, y: i.after.y })
      }

      void markEdited(items.map((i) => i.shapeId))

      syncActiveFromShapeSelection()
      const shapesAfter = mgr.getShapes()

      for (const i of items) {
        const shape = shapesAfter.find((s) => s.id === i.shapeId)

        if (shape) {
          enqueueUpdate(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              shape,
              buildStowedVisibleRectOptions(shape.rotation, 'move'),
              spreadProjection,
              fontLookupRef.current ?? undefined
            ),
            { operation: 'move' }
          )
        }
      }
    },
    [
      pushAction,
      markEdited,
      spreadProjection,
      syncActiveFromShapeSelection,
      enqueueUpdate,
      fontLookupRef,
      shapeManagerRef,
      shapeTextBakeRef,
    ]
  )

  const handleShapeCreated = useCallback(
    (shape: ShapeData, meta?: ShapeHistoryLifecycleMeta) => {
      if (isGroupShape(shape)) {
        // A newly created group is a brand-new element like any other — it needs a real
        // fractionalIndex and a unifiedLayerIds entry, or two things break: (1) buildStowedElement
        // falls back to a placeholder FI derived from the random group id (unrelated to insertion
        // order), scrambling the group's stacking position once the spread reloads and order is
        // reconstructed purely from FI sort; (2) elementZIndexMap has no entry for the group, so
        // its live z-index falls back to `map.size + 1` — pinning it above every other element on
        // the canvas regardless of where it was actually inserted. Mirrors the plain-shape path
        // below.
        let { fractionalIndex } = shape

        if (!fractionalIndex) {
          const lastId = unifiedLayerIdsRef.current[unifiedLayerIdsRef.current.length - 1]
          const lastFI = lastId
            ? shapeManagerRef.current?.getShapes().find((s) => s.id === lastId)?.fractionalIndex
            : undefined

          fractionalIndex = fractionalIndexMidpoint(lastFI ?? '', '')
          shapeManagerRef.current?.updateShape(shape.id, { fractionalIndex })
        }

        const groupWithFI =
          fractionalIndex === shape.fractionalIndex ? shape : { ...shape, fractionalIndex }

        if (!unifiedLayerIdsRef.current.includes(shape.id)) {
          const newOrder = [...unifiedLayerIdsRef.current, shape.id]

          unifiedLayerIdsRef.current = newOrder
          setUnifiedLayerIds(newOrder)
        }

        pushAction({
          type: 'add',
          shape: groupWithFI,
          insertionIndex: meta?.historyInsertIndex,
          historyTxnId: meta?.historyTxnId,
        })

        if (isGroupTxn(meta?.historyTxnId) || meta?.isGroupCreation) {
          const groupId = groupWithFI.id
          const insertIndex = meta?.historyInsertIndex
          const historyTxnId = meta?.historyTxnId

          // POST /group (and the wrap-multi-group /restructure fallback) reference child
          // objectIds that must already exist server-side. Freshly drawn shapes sit in
          // ChangeQueue's 300ms debounce window, while GroupActionQueue flushes on a microtask —
          // without this flush the group request can race ahead of its own children's `add`
          // calls. Mirrors the explicit `await flushQueue()` on the redo-Group / undo-Ungroup path
          // (`runHistoryStep` in use-home-history.ts).
          //
          // That wait can be long (a template's elements are added one request at a time), and an
          // Undo during it removes the group and its children: the creation is then called off, or
          // the request would make an empty group (`pending-group-persist`).
          const pendingPersist = beginPendingGroupPersist(groupId)
          const shouldSend = () => claimPendingGroupPersist(groupId, pendingPersist)

          void (async () => {
            await flushQueue()

            if (!isPendingGroupPersistCurrent(groupId, pendingPersist)) return

            const shapes = shapeManagerRef.current?.getShapes() ?? []
            const textBoxes: TextBoxData[] = []
            const currentGroup = shapes.find((s) => s.id === groupId) ?? groupWithFI

            let queued = false

            try {
              persistForwardGroupApiFromShapes({
                shapes,
                textBoxes,
                group: currentGroup,
                insertIndex,
                historyTxnId,
                projection: spreadProjection,
                markEdited,
                enqueueGroup: (payload) => {
                  queued = true
                  enqueueGroup(payload, { shouldSend })
                },
                enqueueRestructure: (payload) => {
                  queued = true
                  enqueueRestructure(payload, { shouldSend })
                },
                warn: (message, error) => logger.warn(message, error),
              })
            } finally {
              // Nothing was queued (it declined, or threw): the token has no request to be claimed
              // by, so it is released here rather than kept for the session.
              if (!queued) claimPendingGroupPersist(groupId, pendingPersist)
            }
          })()
        }

        return
      }

      // New shapes need a real fractionalIndex reflecting their position in the unified
      // z-order at creation time. Without one, buildStowedElement falls back to a
      // per-object placeholder FI derived from the random objectId — unrelated to insertion
      // order — which scrambles z-order (relative to the square/oval/etc. from this same
      // session) once the spread reloads and order is reconstructed purely from FI sort.
      let { fractionalIndex } = shape

      if (!fractionalIndex) {
        const lastId = unifiedLayerIdsRef.current[unifiedLayerIdsRef.current.length - 1]
        const lastFI = lastId
          ? shapeManagerRef.current?.getShapes().find((s) => s.id === lastId)?.fractionalIndex
          : undefined

        fractionalIndex = fractionalIndexMidpoint(lastFI ?? '', '')
        shapeManagerRef.current?.updateShape(shape.id, { fractionalIndex })
      }

      const shapeWithFI =
        fractionalIndex === shape.fractionalIndex ? shape : { ...shape, fractionalIndex }

      void markEdited([shape.id])
      pushAction({
        type: 'add',
        shape: shapeWithFI,
        insertionIndex: meta?.historyInsertIndex,
        historyTxnId: meta?.historyTxnId,
      })
      enqueueAdd(
        buildStowedShapeElement(
          shapeTextBakeRef.current,
          shapeWithFI,
          undefined,
          spreadProjection,
          fontLookupRef.current ?? undefined
        )
      )
      if (!unifiedLayerIdsRef.current.includes(shape.id)) {
        const newOrder = [...unifiedLayerIdsRef.current, shape.id]

        unifiedLayerIdsRef.current = newOrder
        setUnifiedLayerIds(newOrder)
      }

      // A newly created ClikArt (tray drop, size-warning replacement, paste, duplicate) marks its
      // gallery card "Placed". Include `shapeWithFI` in case the manager hasn't listed it yet.
      if (resolvePlacedClikArtId(shapeWithFI)) {
        const current = shapeManagerRef.current?.getShapes() ?? []

        reconcileClikArtPlacementsWithShapes(
          current.some((s) => s.id === shapeWithFI.id) ? current : [...current, shapeWithFI],
          spreadPlacementBounds,
          spreadIdRef.current
        )
      }
    },
    [
      spreadPlacementBounds,
      enqueueAdd,
      enqueueGroup,
      enqueueRestructure,
      flushQueue,
      isGroupShape,
      isGroupTxn,
      markEdited,
      pushAction,
      spreadProjection,
      spreadIdRef,
      fontLookupRef,
      setUnifiedLayerIds,
      shapeManagerRef,
      shapeTextBakeRef,
      unifiedLayerIdsRef,
    ]
  )

  /**
   * Tracks the most recent "Edit Path" auto-conversion (Shapes-panel preset -> pen path) so that
   * exiting node-edit mode having changed nothing can silently erase the conversion instead of
   * leaving the shape permanently turned into a path. Cleared whenever node-edit mode is entered
   * on a shape that was already a path (no conversion happened this time).
   */
  const pendingPathConversionRef = useRef<{
    shapeId: string
    convertedShape: ShapeData
    historyTxnId: string
  } | null>(null)

  /**
   * Called on every node-edit-mode exit (see `onNodeEditModeChange` below). If `shapeId` is the
   * shape a preset-to-path conversion just created, and its geometry is still exactly what the
   * conversion produced (no drag, no node add/delete/move), erases that conversion's history
   * entry and reverts the shape via the same path a manual Ctrl+Z of it would take — so the
   * preset shape is never left permanently converted just for having been peeked at.
   */
  const revertPathConversionIfUnedited = useCallback(
    (shapeId: string) => {
      const pending = pendingPathConversionRef.current

      if (!pending || pending.shapeId !== shapeId) return

      pendingPathConversionRef.current = null

      const liveShape = shapeManagerRef.current?.getShapes().find((s) => s.id === shapeId)

      if (!liveShape) return

      const unchanged =
        liveShape.x === pending.convertedShape.x &&
        liveShape.y === pending.convertedShape.y &&
        liveShape.width === pending.convertedShape.width &&
        liveShape.height === pending.convertedShape.height &&
        liveShape.path === pending.convertedShape.path &&
        JSON.stringify(liveShape.pathCommands) ===
          JSON.stringify(pending.convertedShape.pathCommands)

      if (!unchanged) return

      // Only reverts if the conversion is still exactly at the top of the undo stack — if any
      // other action landed on top of it, leave both alone rather than risk erasing the wrong one.
      const reverted = popIfMatches(
        (action) => action.type === 'convertToPath' && action.historyTxnId === pending.historyTxnId
      )

      if (reverted) {
        void runHistoryStep(reverted, 'undo')
      }
    },
    [popIfMatches, runHistoryStep, shapeManagerRef]
  )

  const handleEditPath = useCallback(
    (shapeId: string) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const shapes = mgr.getShapes()
      const shapeIndex = shapes.findIndex((s) => s.id === shapeId)

      if (shapeIndex === -1) return

      const shape = shapes[shapeIndex]

      // WMPCM-8911: a group has no path geometry of its own — the Object panel and context menu
      // already disable the Edit Path affordance for a group selection, but guard the handler
      // itself too rather than relying solely on the UI never calling it with a group's id.
      if (isGroupShape(shape)) return

      if (shape.type === 'path') {
        pendingPathConversionRef.current = null
        centerCanvasRef.current?.enterNodeEditMode(shape)
        return
      }

      if (shape.locked) return

      if (shape.frozen) {
        toast.error(FROZEN_CONVERT_MESSAGE)
        return
      }

      const convertedShape = convertShapeToPenPath(shape)
      const historyTxnId = `convert-to-path:${shape.id}`

      mgr.updateShape(shape.id, convertedShape)

      void markEdited([shape.id])

      pushAction({
        type: 'convertToPath',
        shape,
        convertedShape,
        insertionIndex: shapeIndex,
        historyTxnId,
      })

      enqueueDelete(shape.id, { meta: { historyTxnId } })
      enqueueAdd(
        buildStowedShapeElement(
          shapeTextBakeRef.current,
          convertedShape,
          undefined,
          spreadProjection,
          fontLookupRef.current ?? undefined
        ),
        {
          isPenElement: true,
          meta: { historyTxnId },
        }
      )

      pendingPathConversionRef.current = { shapeId: shape.id, convertedShape, historyTxnId }
      centerCanvasRef.current?.enterNodeEditMode(convertedShape)
    },
    [
      enqueueAdd,
      enqueueDelete,
      isGroupShape,
      markEdited,
      pushAction,
      spreadProjection,
      centerCanvasRef,
      fontLookupRef,
      shapeManagerRef,
      shapeTextBakeRef,
    ]
  )

  /** "Crop" button in the Photos panel — enters crop mode for the single selected photo shape. */
  const handleCropPhoto = useCallback(() => {
    const mgr = shapeManagerRef.current

    if (!mgr) return

    const selectedShapeIds = mgr.getSelectedShapeIds()

    if (selectedShapeIds.length !== 1) return

    const shape = mgr.getShapes().find((s) => s.id === selectedShapeIds[0])

    if (!shape) return

    centerCanvasRef.current?.enterCropMode(shape)
  }, [centerCanvasRef, shapeManagerRef])

  /** Photo Effects popover open state — shared by LH panel, floating toolbar, and context menu (WMPCM-9470). */
  const [photoEffectsOpen, setPhotoEffectsOpen] = useState(false)

  const handlePhotoEffects = useCallback(() => {
    setPhotoEffectsOpen((open) => !open)
  }, [])

  const handleDeleteGestureScope = useCallback(
    ({ txnId, ids }: { txnId: string; ids: readonly string[] }) => {
      deleteGestureScopeRef.current = { txnId, ids: new Set(ids) }
    },
    [deleteGestureScopeRef]
  )

  /**
   * Menu → Object → Convert Text to Shape enablement. Enabled whenever AT LEAST ONE selected
   * object is a structurally-eligible text frame (unlocked, unfrozen, not text-on-a-path, not
   * part of a linked chain) — a multi-selection converts every eligible member and silently
   * skips the rest (structural ineligibility is a menu-time concern per the acceptance criteria;
   * the 24pt/50-character rule is checked separately at click time, with a toast on failure).
   *
   * Reads `activeObjectsForMenus` (the shape snapshots), NOT `selectedTextBoxesForMenus`: a text
   * frame is a shape carrying `paragraphs` since the text unification, and
   * `getCurrentSelectionSnapshots` returns `textSnapshots: []` unconditionally. Keying off the text
   * list pinned this expression to `true`, greying the row out for every selection and leaving the
   * chain clause unreachable — which is what WMPCM-9190 reported. Do not "fix" that by populating
   * `textSnapshots`; it is shared by eight other gates in home's hooks.
   *
   * Chain membership goes through `isLinkedTextFrame` (inside `isTextToShapeStructurallyBlocked`),
   * the one canonical predicate (WMPCM-8969) — a previous `box.uid != null` guard elsewhere in this
   * file once admitted a `uid: 0` frame as chain key `"0"`.
   */
  const convertTextToShapeDisabledForMenus =
    activeObjectsForMenus.length === 0 ||
    !activeObjectsForMenus.some((obj) => !isTextToShapeStructurallyBlocked(obj, textChains))

  /**
   * Menu → Object → Convert Text to Shape / context menu action. For every selected,
   * structurally-eligible text frame (see the gate above): checks the 24pt-minimum /
   * 50-character-maximum rule, converts the ones that pass (client-side glyph outline extraction
   * — see `text-to-shape-outline.ts` — vs. legacy's server round-trip), and leaves the rest as
   * editable text with one toast covering the whole batch. Mirrors `handleEditPath`'s delete-old/
   * add-new + `convertToPath` undo pattern exactly, since a kind change (text shape → path shape)
   * needs the same ordering to avoid ChangeQueue coalescing issues.
   *
   * Structurally-ineligible members of a mixed selection are skipped silently (already greyed out
   * of being clicked on their own; see the gate above) — only the size/length rule gets a toast,
   * per the acceptance criteria.
   */
  const handleConvertTextToShape = useCallback(() => {
    const mgr = shapeManagerRef.current

    if (!mgr) return

    const selectedIds = new Set(mgr.getSelectedShapeIds())
    const shapes = mgr.getShapes()
    const eligible = shapes.filter(
      (s) => selectedIds.has(s.id) && !isTextToShapeStructurallyBlocked(s, textChainsRef.current)
    )

    if (eligible.length === 0) {
      toast.error(TEXT_TO_SHAPE_NO_SELECTION_MESSAGE)
      return
    }

    const toConvert: ShapeData[] = []
    let anySizeOrLengthFailure = false

    for (const shape of eligible) {
      if (meetsTextToShapeSizeAndLengthRule(shape)) {
        toConvert.push(shape)
      } else {
        anySizeOrLengthFailure = true
      }
    }

    if (anySizeOrLengthFailure) {
      toast.error(TEXT_TO_SHAPE_INELIGIBLE_MESSAGE, { duration: 5000 })
    }

    if (toConvert.length === 0) return

    // Locked once, up front, for every shape in this batch — not per-shape inside the loop below.
    // `useElementsLock` tracks a single "currently locked" id set; calling `markEdited` again with a
    // DIFFERENT (single-shape) id set mid-loop unlocks whatever was locked by the previous shape
    // before locking the new one (`ensureLocked`'s `isSameIdSet` check), and that unlock's
    // `beforeUnlock` immediately force-flushes the ChangeQueue out of its normal debounce schedule.
    // That out-of-schedule flush can send a shape's queued `add` (still sitting in `pending`) before
    // its own `delete` — sent moments earlier out-of-band via `sendAhead`, for the same reused
    // objectId — has actually reached the server, so the delete can land AFTER the add and wipe out
    // the very shape it just created. Converting a single shape never hit this (only one lock call,
    // nothing to unlock mid-flight); converting two or more intermittently lost one at random,
    // depending on network timing (WMPCM-9637 follow-up).
    void markEdited(toConvert.map((s) => s.id))

    void (async () => {
      const fontFamilies = fontFamiliesRef.current
      let convertedCount = 0
      let lastConvertedShape: ShapeData | null = null
      let anySkippedCharacters = false

      for (const shape of toConvert) {
        const outline = await buildTextShapeOutline(shape, fontFamilies)

        if (!outline) {
          logger.warn('Text-to-shape: failed to extract a glyph outline, skipping', {
            shapeId: shape.id,
          })
          continue
        }

        if (outline.skippedCharacters.length > 0) anySkippedCharacters = true

        const currentShapes = shapeManagerRef.current?.getShapes() ?? []
        const shapeIndex = currentShapes.findIndex((s) => s.id === shape.id)

        if (shapeIndex === -1) continue

        const convertedShape = buildShapeFromTextOutline(shape, outline)
        const historyTxnId = `convert-text-to-shape:${shape.id}`

        mgr.updateShape(shape.id, convertedShape)

        pushAction({
          type: 'convertToPath',
          shape,
          convertedShape,
          insertionIndex: shapeIndex,
          historyTxnId,
        })

        enqueueDelete(shape.id, { meta: { historyTxnId } })
        enqueueAdd(
          buildStowedElement(
            convertedShape,
            undefined,
            spreadProjection,
            fontLookupRef.current ?? undefined
          ),
          { isPenElement: true, meta: { historyTxnId } }
        )

        convertedCount++
        lastConvertedShape = convertedShape
      }

      if (convertedCount === 0) return

      setActiveItem('select')
      leftPanelRef.current?.expandAndOpenObject?.()

      if (convertedCount === 1 && lastConvertedShape) {
        centerCanvasRef.current?.enterNodeEditMode(lastConvertedShape)
      }

      if (anySkippedCharacters) {
        toast.error(
          'Some characters could not be converted (a font issue) and were left out of the shape.',
          { duration: 5000 }
        )
      }
    })()
  }, [
    enqueueAdd,
    enqueueDelete,
    markEdited,
    pushAction,
    spreadProjection,
    centerCanvasRef,
    fontFamiliesRef,
    fontLookupRef,
    leftPanelRef,
    shapeManagerRef,
    textChainsRef,
    setActiveItem,
  ])

  /**
   * Object > Path Operations > Text on Path (WMPCM-9514 AC-2). Enabled for exactly one non-group
   * object, matching the context menus' own `contextMenuTextOnPathTarget`.
   *
   * Reads `activeObjectsForMenus` (the live ShapeManager snapshot), never `selectedTextBoxesForMenus`
   * — that list is hardcoded empty, and keying a gate off it pins the row disabled for every
   * selection (WMPCM-9190).
   */
  const textOnPathDisabledForMenus = (() => {
    const [only] = activeObjectsForMenus

    if (activeObjectsForMenus.length !== 1 || only == null) return true

    return isGroupShape(only)
  })()

  // WMPCM-8911: a group has no path geometry of its own, so Edit Path/Clipping Path is disabled
  // whenever the (single-object) selection IS a group. Selecting an object inside a group
  // (isolation mode) resolves that child shape here instead, so it stays unaffected.
  const editPathDisabledForMenus = (() => {
    const [only] = activeObjectsForMenus

    if (activeObjectsForMenus.length !== 1 || only == null) return true

    return isGroupShape(only)
  })()

  const runTextOnPathCommand = useCallback(() => {
    centerCanvasRef.current?.enterTextOnPathMode()
  }, [centerCanvasRef])

  /**
   * The persist step photo fill, ClikArt fill and photo removal share: swaps the shape's
   * `photoFill`, marks it edited, enqueues the stowed update and pushes the undo action. Returns
   * `false`, having enqueued and pushed nothing, when the shape is gone after the update; callers
   * run their own follow-up (placement tracking, selection resync) only on `true`.
   */
  const commitShapePhotoFill = useCallback(
    (
      mgr: NonNullable<typeof shapeManagerRef.current>,
      shape: ShapeData,
      photoFill: ShapeData['photoFill'],
      historyTxnId: string
    ): boolean => {
      const shapeId = shape.id
      const before = { photoFill: shape.photoFill }

      mgr.updateShape(shapeId, { photoFill })
      void markEdited([shapeId])

      const updatedShape = mgr.getShapes().find((s) => s.id === shapeId)

      if (!updatedShape) return false

      const stowedPayload = buildStowedShapeElement(
        shapeTextBakeRef.current,
        updatedShape,
        buildStowedVisibleRectOptions(updatedShape.rotation, 'update'),
        spreadProjection,
        fontLookupRef.current ?? undefined
      )

      enqueueUpdate(stowedPayload, { operation: 'update' })
      pushAction({ type: 'update', shapeId, before, after: { photoFill }, historyTxnId })

      return true
    },
    [
      enqueueUpdate,
      markEdited,
      pushAction,
      spreadProjection,
      fontLookupRef,
      shapeManagerRef,
      shapeTextBakeRef,
    ]
  )

  const applyPhotoFillToShape = useCallback(
    (
      shapeId: string,
      photo: PhotoCandid,
      mgr: NonNullable<typeof shapeManagerRef.current>,
      intrinsicSize?: { width: number; height: number } | null,
      options?: { preserveTransform?: boolean; historyTxnId?: string }
    ) => {
      const shapes = mgr.getShapes()
      const shape = shapes.find((s) => s.id === shapeId)

      if (!shape) return

      if (supportsPhotoFill(shape)) {
        let photoFill = buildShapePhotoFill(photo, intrinsicSize ?? undefined)

        if (options?.preserveTransform && shape.photoFill) {
          photoFill = {
            ...photoFill,
            offsetX: shape.photoFill.offsetX,
            offsetY: shape.photoFill.offsetY,
            scale: shape.photoFill.scale,
            ...(shape.photoFill.rotation != null ? { rotation: shape.photoFill.rotation } : {}),
            ...(shape.photoFill.flippedX != null ? { flippedX: shape.photoFill.flippedX } : {}),
            ...(shape.photoFill.flippedY != null ? { flippedY: shape.photoFill.flippedY } : {}),
          }
        }

        if (
          commitShapePhotoFill(
            mgr,
            shape,
            photoFill,
            options?.historyTxnId ?? `fill-shape-with-photo:${shapeId}`
          )
        ) {
          // Placed badge / page tracking — mirrors the standalone-drop path
          // (`placePhotoAtCanvas` in `photo-placement-context.tsx`) and the removal path below
          // (`handleRemovePhotoFromShape`): update the local optimistic override, then tell the
          // tray to re-fetch so its server-cached `placedFlag` catches up too.
          reconcilePhotoPlacementOverridesWithShapes(
            mgr.getShapes(),
            spreadPlacementBounds,
            spreadIdRef.current
          )
          notifyPhotoPlacementChangedOnSpread()
        }
      }
    },
    [commitShapePhotoFill, shapeManagerRef, spreadIdRef, spreadPlacementBounds]
  )

  // One placement at a time: each reads the spread's top index and next free uid up front and moves
  // them forward only once its response is in, so two in flight would reuse both. The runner holds
  // its queue, so it lives in a ref: a `useMemo` cache may be dropped, which would end the ordering.
  const placementRunnerRef = useRef<ReturnType<typeof createSerialRunner> | null>(null)

  placementRunnerRef.current ??= createSerialRunner()

  /** A template dropped on the spread (US-13): see `placeTemplate`. */
  const handlePlaceTemplate = useCallback(
    (template: TemplateListItem) =>
      placementRunnerRef.current!(() =>
        placeTemplate(template, {
          shapeManagerRef,
          spreadIdRef,
          unifiedLayerIdsRef,
          setUnifiedLayerIds,
          spreadProjection,
          isGroupShape,
          textChainsRef,
          setTextChains,
          pushAction,
          persistChainRider,
          mintTextFrameUid,
          getTextFrameUidHighWater: () => mintedTextFrameUidHighWaterRef.current,
          reserveTextFrameUidsThrough,
          resolveImagePreviews: resolveTemplateImagePreviews,
          loadTemplateLayer: templatesService.getTemplateLayer,
        })
      ),
    [
      isGroupShape,
      mintedTextFrameUidHighWaterRef,
      mintTextFrameUid,
      persistChainRider,
      pushAction,
      reserveTextFrameUidsThrough,
      setTextChains,
      setUnifiedLayerIds,
      shapeManagerRef,
      spreadIdRef,
      spreadProjection,
      textChainsRef,
      unifiedLayerIdsRef,
    ]
  )

  const handleRemovePhotoFromShape = useCallback(
    (shapeId: string) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const shape = mgr.getShapes().find((s) => s.id === shapeId)

      if (!shape || !shape.photoFill) return

      // A ClikArt that is its own object would be left behind as an empty block, so Remove ClikArt
      // deletes it, through the same canvas delete the Delete key uses (history, locks, the
      // gallery's Placed badge). One that sits in a shape the user drew only loses its art
      // (WMPCM-13334).
      //
      // A locked or frozen object can't be deleted, and the canvas delete would only show a
      // "can't delete" notice. It keeps the old behavior instead — the art is cleared — as does any
      // case where the delete reports that it removed nothing, or the shape isn't the sole selection.
      if (isStandaloneClikArtShape(shape) && !shape.locked && !shape.frozen) {
        const selectedIds = mgr.getSelectedShapeIds()

        if (
          selectedIds.length === 1 &&
          selectedIds[0] === shapeId &&
          centerCanvasRef.current?.deleteSelection()
        ) {
          return
        }
      }

      const removedPhotoFill = shape.photoFill

      if (commitShapePhotoFill(mgr, shape, undefined, `remove-photo-from-shape:${shapeId}`)) {
        reconcilePhotoPlacementOverridesWithShapes(
          mgr.getShapes(),
          spreadPlacementBounds,
          spreadIdRef.current
        )
        reconcileClikArtPlacementsWithShapes(
          mgr.getShapes(),
          spreadPlacementBounds,
          spreadIdRef.current
        )

        // The reconcile above only clears the LOCAL optimistic override (if one existed) — it
        // doesn't refresh the Photos tray's own server-cached `placedFlag`, which is what
        // actually drives the "Placed" badge for a photo that was already placed before this
        // session started (the common case). Tell the tray to re-fetch so a photo removed here —
        // possibly its last placement in the whole book — stops showing as placed.
        if (removedPhotoFill.imageId) {
          notifyPhotoPlacementChangedOnSpread()
        }

        // The shape mutated in place via `mgr.updateShape` — refresh the `activeObjects` mirror
        // so anything deriving "is a photo selected" from it (e.g. the left panel's auto
        // Photos/Object section switch) sees the photo is gone.
        syncActiveFromShapeSelection()
      }
    },
    [
      spreadPlacementBounds,
      centerCanvasRef,
      commitShapePhotoFill,
      shapeManagerRef,
      syncActiveFromShapeSelection,
      spreadIdRef,
    ]
  )

  /** "Remove photo" button in the Photos panel — same action as the photo context menu's
   * "Remove Photo", applied to the single selected photo shape. */
  const handleRemovePhotoFromPhotosPanel = useCallback(() => {
    const mgr = shapeManagerRef.current

    if (!mgr) return

    const selectedShapeIds = mgr.getSelectedShapeIds()

    if (selectedShapeIds.length !== 1) return

    handleRemovePhotoFromShape(selectedShapeIds[0])
  }, [handleRemovePhotoFromShape, shapeManagerRef])

  const removingBackgroundShapeIdsRef = useRef<Set<string>>(new Set())
  const [removingBackgroundShapeIds, setRemovingBackgroundShapeIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )

  /**
   * WMPCM-1803 — Remove Background via Larry API (Picsart on the server).
   * Replaces the shape's photoFill with the edited candid; undo restores the previous fill.
   */
  const handleRemoveBackground = useCallback(
    async (shapeId?: string) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const targetId = shapeId ?? mgr.getSelectedShapeIds()[0]

      if (!targetId || removingBackgroundShapeIdsRef.current.has(targetId)) return

      const shape = mgr.getShapes().find((s) => s.id === targetId)
      const identity = shape ? resolvePlacedPhotoIdentity(shape) : null

      if (!shape || !identity?.imageId || shape.locked || isPortraitPlacedPhoto(shape)) return

      removingBackgroundShapeIdsRef.current.add(targetId)
      setRemovingBackgroundShapeIds(new Set(removingBackgroundShapeIdsRef.current))

      try {
        const edited = await photosService.removeBackground(identity.imageId)
        const intrinsicSize =
          edited.pixelWidth != null && edited.pixelHeight != null
            ? { width: edited.pixelWidth, height: edited.pixelHeight }
            : null

        applyPhotoFillToShape(targetId, edited, mgr, intrinsicSize, {
          preserveTransform: true,
          historyTxnId: `remove-background:${targetId}`,
        })
        reconcilePhotoPlacementOverridesWithShapes(
          mgr.getShapes(),
          spreadPlacementBounds,
          spreadIdRef.current
        )
        notifyPhotoPlacementChangedOnSpread()
        void queryClient.invalidateQueries({ queryKey: queryKeys.photos.all })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to remove background'

        toast.error(message)
      } finally {
        removingBackgroundShapeIdsRef.current.delete(targetId)
        setRemovingBackgroundShapeIds(new Set(removingBackgroundShapeIdsRef.current))
      }
    },
    [applyPhotoFillToShape, queryClient, shapeManagerRef, spreadIdRef, spreadPlacementBounds]
  )

  const handleRemoveBackgroundFromPhotosPanel = useCallback(() => {
    void handleRemoveBackground()
  }, [handleRemoveBackground])

  const isRemovingBackgroundForSelection = Boolean(
    selectedPhotoId && removingBackgroundShapeIds.has(selectedPhotoId)
  )

  /**
   * A photo drop landed on a target that refuses photos (WMPCM-8516). Raised both from the drag
   * session, which refuses before `onFillShape` is ever called, and from the `handleFillShapeWithPhoto`
   * re-checks — the two are mutually exclusive on any one drop, so the toast cannot double-fire.
   */
  const handlePhotoDropBlocked = useCallback((_reason: PhotoDropBlockedReason) => {
    toast.error(LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE)
  }, [])

  const handleFillShapeWithPhoto = useCallback(
    (
      shapeId: string,
      photo: PhotoCandid,
      _kindId: number,
      _dropCanvasPoint: { canvasX: number; canvasY: number },
      intrinsicSize: { width: number; height: number } | null
    ) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const shape = mgr.getShapes().find((s) => s.id === shapeId)

      if (!shape) return

      // Filling a chain member sets `photoFill`, which makes `isShapeTextFrame` false — photo fill
      // and shape text are mutually exclusive on the wire and the photo wins — so the frame would
      // drop out of the chain while the chain still lists its uid, taking its text with it
      // (WMPCM-8516). One check covers every frame now that a Text-tool frame is a shape.
      //
      // `hitFillableShapeAtPoint` already refuses this drop before it reaches here; this is
      // defence in depth for a chain edit that lands between pointermove and pointerup, matching
      // the runtime re-check `convertTextBoxToPathShape` keeps for the same reason. Placed ahead
      // of the resolution test so a linked frame opens no dialog at all — the drop is simply not
      // an available operation on it.
      if (isPhotoDropBlockedByTextChain(shape, textChainsRef.current)) {
        handlePhotoDropBlocked('linked-text-frame')
        return
      }

      // Resolution check using intrinsicSize already loaded during drag — synchronous, no delay.
      // Matches legacy: only one dialog ever shows — "Photo Size" if the photo is too small (the
      // replace-text confirmation never appears in that case), or the replace-text confirmation
      // if the photo is a good enough fit.
      if (
        intrinsicSize &&
        !meetsMinimumResolution(
          intrinsicSize.width,
          intrinsicSize.height,
          shape.width,
          shape.height
        )
      ) {
        setPhotoSizeWarningState({
          scenario: 'drop',
          photo,
          targetCenter: { canvasX: shape.x + shape.width / 2, canvasY: shape.y + shape.height / 2 },
          intrinsicSize,
        })
        return
      }

      // Photo fill replaces `paragraphs` on the wire (oneof) — confirm before destroying existing
      // text. A text-tool frame and a hand-drawn shape someone typed a caption into are the same
      // risk here, so this gates on `isShapeTextFrame`, not a text-tool-only kind check.
      if (isShapeTextFrame(shape)) {
        setTextToImageWarningState({ shapeId, photo, intrinsicSize })
        return
      }

      applyPhotoFillToShape(shapeId, photo, mgr, intrinsicSize)
    },
    [
      applyPhotoFillToShape,
      handlePhotoDropBlocked,
      shapeManagerRef,
      setPhotoSizeWarningState,
      setTextToImageWarningState,
      textChainsRef,
    ]
  )

  const handleTextToImageWarningContinue = useCallback(() => {
    const state = textToImageWarningState

    setTextToImageWarningState(null)

    if (!state) return

    const mgr = shapeManagerRef.current

    if (!mgr) return

    // Resolution was already checked (and confirmed to pass) in handleFillShapeWithPhoto before
    // this confirmation was ever shown.
    applyPhotoFillToShape(state.shapeId, state.photo, mgr, state.intrinsicSize)
  }, [applyPhotoFillToShape, shapeManagerRef, textToImageWarningState, setTextToImageWarningState])

  const handleTextToImageWarningCancel = useCallback(() => {
    setTextToImageWarningState(null)
  }, [setTextToImageWarningState])

  const handlePhotoSizeWarningContinue = useCallback(() => {
    const state = photoSizeWarningState

    setPhotoSizeWarningState(null)

    if (!state) return

    const mgr = shapeManagerRef.current

    if (!mgr) return

    // Place a new photo box centered on the original target's own center (the original shape/
    // text box stays untouched), sized to the photo's own resolution — matches legacy exactly.
    const objectId = crypto.randomUUID().replace(/-/g, '')
    const { bleedWidth, bleedHeight } = resolvedLayout
    const size = computeLegacyReplacementFrameSize(
      state.intrinsicSize?.width,
      state.intrinsicSize?.height
    )
    const newShape = buildPlacedPhotoShape({
      objectId,
      photo: state.photo,
      centerX: state.targetCenter.canvasX,
      centerY: state.targetCenter.canvasY,
      canvasWidth: bleedWidth,
      canvasHeight: bleedHeight,
      intrinsicWidth: state.intrinsicSize?.width,
      intrinsicHeight: state.intrinsicSize?.height,
      size,
    })

    mgr.addShapeData({ ...newShape, placementAnimateIn: true }, { selectAfterCreate: true })
    reconcilePhotoPlacementOverridesWithShapes(
      mgr.getShapes(),
      spreadPlacementBounds,
      spreadIdRef.current
    )
    notifyPhotoPlacementChangedOnSpread()
  }, [
    photoSizeWarningState,
    resolvedLayout,
    shapeManagerRef,
    setPhotoSizeWarningState,
    spreadIdRef,
    spreadPlacementBounds,
  ])

  const handlePhotoSizeWarningCancel = useCallback(() => {
    setPhotoSizeWarningState(null)
  }, [setPhotoSizeWarningState])

  /**
   * ClikArt-side counterpart of `applyPhotoFillToShape` (WMPCM-11014) — same shape, sourced from a
   * `ClikArtItem` via `buildShapeClikArtFill` instead of a `PhotoCandid` via `buildShapePhotoFill`.
   * Updates the gallery's Placed badge (WMPCM-11057) via `reconcileClikArtPlacementsWithShapes`.
   */
  const applyClikArtFillToShape = useCallback(
    (
      shapeId: string,
      item: ClikArtItem,
      mgr: NonNullable<typeof shapeManagerRef.current>,
      intrinsicSize?: { width: number; height: number } | null
    ) => {
      const shapes = mgr.getShapes()
      const shape = shapes.find((s) => s.id === shapeId)

      if (!shape) return

      if (supportsPhotoFill(shape)) {
        const photoFill = buildShapeClikArtFill(item, intrinsicSize ?? undefined)

        if (commitShapePhotoFill(mgr, shape, photoFill, `fill-shape-with-clikart:${shapeId}`)) {
          reconcileClikArtPlacementsWithShapes(
            mgr.getShapes(),
            spreadPlacementBounds,
            spreadIdRef.current
          )
        }
      }
    },
    [spreadPlacementBounds, commitShapePhotoFill, shapeManagerRef, spreadIdRef]
  )

  /**
   * A ClikArt drop landed on a target that refuses ClikArt (mirrors WMPCM-8516's photo handling).
   * Raised both from the drag session, which refuses before `onFillShape` is ever called, and from
   * `handleFillShapeWithClikArt`'s re-check — mutually exclusive on any one drop.
   */
  const handleClikArtDropBlocked = useCallback((_reason: ClikArtDropBlockedReason) => {
    toast.error(LINKED_TEXT_CLIKART_DROP_BLOCKED_MESSAGE)
  }, [])

  const handleFillShapeWithClikArt = useCallback(
    (
      shapeId: string,
      item: ClikArtItem,
      _kindId: number,
      _dropCanvasPoint: { canvasX: number; canvasY: number },
      intrinsicSize: { width: number; height: number } | null
    ) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const shape = mgr.getShapes().find((s) => s.id === shapeId)

      if (!shape) return

      // `hitFillableShapeAtPoint` already refuses this drop before it reaches here; this is
      // defence in depth for a chain edit that lands between pointermove and pointerup — mirrors
      // `handleFillShapeWithPhoto`'s own re-check for the same reason.
      if (isClikArtDropBlockedByTextChain(shape, textChainsRef.current)) {
        handleClikArtDropBlocked('linked-text-frame')
        return
      }

      // Only one dialog ever shows — "ClikArt Size" if the art is too small (the replace-text
      // confirmation never appears in that case), or the replace-text confirmation if the art is a
      // good enough fit — mirrors `handleFillShapeWithPhoto`'s own ordering exactly.
      if (
        intrinsicSize &&
        !meetsMinimumResolution(
          intrinsicSize.width,
          intrinsicSize.height,
          shape.width,
          shape.height
        )
      ) {
        setClikArtSizeWarningState({
          scenario: 'drop',
          item,
          targetCenter: { canvasX: shape.x + shape.width / 2, canvasY: shape.y + shape.height / 2 },
          intrinsicSize,
        })
        return
      }

      // ClikArt fill replaces `paragraphs` on the wire (oneof) — confirm before destroying
      // existing text, same rationale as the photo path.
      if (isShapeTextFrame(shape)) {
        setClikArtTextToImageWarningState({ shapeId, item, intrinsicSize })
        return
      }

      applyClikArtFillToShape(shapeId, item, mgr, intrinsicSize)
    },
    [
      applyClikArtFillToShape,
      handleClikArtDropBlocked,
      shapeManagerRef,
      setClikArtSizeWarningState,
      setClikArtTextToImageWarningState,
      textChainsRef,
    ]
  )

  const handleClikArtTextToImageWarningContinue = useCallback(() => {
    const state = clikArtTextToImageWarningState

    setClikArtTextToImageWarningState(null)

    if (!state) return

    const mgr = shapeManagerRef.current

    if (!mgr) return

    applyClikArtFillToShape(state.shapeId, state.item, mgr, state.intrinsicSize)
  }, [
    applyClikArtFillToShape,
    shapeManagerRef,
    clikArtTextToImageWarningState,
    setClikArtTextToImageWarningState,
  ])

  const handleClikArtTextToImageWarningCancel = useCallback(() => {
    setClikArtTextToImageWarningState(null)
  }, [setClikArtTextToImageWarningState])

  const handleClikArtSizeWarningContinue = useCallback(() => {
    const state = clikArtSizeWarningState

    setClikArtSizeWarningState(null)

    if (!state) return

    const mgr = shapeManagerRef.current

    if (!mgr) return

    // Place a new ClikArt box centered on the original target's own center (the original shape
    // stays untouched), sized to the art's own resolution — mirrors
    // `handlePhotoSizeWarningContinue` exactly.
    const objectId = crypto.randomUUID().replace(/-/g, '')
    const { bleedWidth, bleedHeight } = resolvedLayout
    const size = computeLegacyReplacementFrameSize(
      state.intrinsicSize?.width,
      state.intrinsicSize?.height
    )
    const newShape = buildPlacedClikArtShape({
      objectId,
      item: state.item,
      centerX: state.targetCenter.canvasX,
      centerY: state.targetCenter.canvasY,
      canvasWidth: bleedWidth,
      canvasHeight: bleedHeight,
      intrinsicWidth: state.intrinsicSize?.width,
      intrinsicHeight: state.intrinsicSize?.height,
      size,
    })

    mgr.addShapeData({ ...newShape, placementAnimateIn: true }, { selectAfterCreate: true })
  }, [clikArtSizeWarningState, resolvedLayout, shapeManagerRef, setClikArtSizeWarningState])

  const handleClikArtSizeWarningCancel = useCallback(() => {
    setClikArtSizeWarningState(null)
  }, [setClikArtSizeWarningState])

  // Bridge ref: useHomeShapePersistence (hook 11) assigns this after it defines
  // handleBatchShapesCommitted, so the color-fill handler can call it without
  // reordering the hooks.
  const handleBatchShapesCommittedRef = useRef<OnBatchShapesCommitted>(() => {})

  const handleFillShapeWithColor = useCallback(
    (_shapeId: string, items: BatchShapeTransformItem[]) => {
      handleBatchShapesCommittedRef.current(items, 'update')
    },
    []
  )

  return {
    photoEffectsOpen,
    setPhotoEffectsOpen,
    removingBackgroundShapeIds,
    setRemovingBackgroundShapeIds,
    handleShapeSelectionHistory,
    handleShapesNudge,
    handleShapeCreated,
    pendingPathConversionRef,
    revertPathConversionIfUnedited,
    handleEditPath,
    handleCropPhoto,
    handlePhotoEffects,
    handleDeleteGestureScope,
    convertTextToShapeDisabledForMenus,
    handleConvertTextToShape,
    textOnPathDisabledForMenus,
    editPathDisabledForMenus,
    runTextOnPathCommand,
    applyPhotoFillToShape,
    handleRemovePhotoFromShape,
    handleRemovePhotoFromPhotosPanel,
    removingBackgroundShapeIdsRef,
    handleRemoveBackground,
    handleRemoveBackgroundFromPhotosPanel,
    isRemovingBackgroundForSelection,
    handlePhotoDropBlocked,
    handleFillShapeWithPhoto,
    handleTextToImageWarningContinue,
    handleTextToImageWarningCancel,
    handlePhotoSizeWarningContinue,
    handlePhotoSizeWarningCancel,
    applyClikArtFillToShape,
    handleClikArtDropBlocked,
    handleFillShapeWithClikArt,
    handlePlaceTemplate,
    handleClikArtTextToImageWarningContinue,
    handleClikArtTextToImageWarningCancel,
    handleClikArtSizeWarningContinue,
    handleClikArtSizeWarningCancel,
    handleBatchShapesCommittedRef,
    handleFillShapeWithColor,
  }
}
